import { Button } from '@/components/ui/button';
import { useState  } from 'react';
import type {FormEvent} from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Page } from '@/components/layout/page';
import { DevNote } from '@/components/ui/dev-note';
import { Input } from '@/components/ui/field';
import { Alert, Card, CardBody, Container, Section } from '@/components/ui/primitives';
import { useSession } from '@/hooks/use-session';
import { fieldErrors, isApiError, presentError } from '@/lib/errors';
import { paths } from '@/lib/paths';

/**
 * Connexion — `POST /auth/login`.
 *
 * **Il n'y a pas de page d'inscription sur ce site, et c'est volontaire :**
 * l'API n'expose aucun endpoint de création de compte. Un compte naît d'un
 * paiement réussi, et son titulaire reçoit alors un email pour choisir son
 * mot de passe. Cette page ne sert donc qu'aux clients qui reviennent.
 *
 * Après une connexion réussie, le visiteur est renvoyé là où il voulait
 * aller : `location.state.from`, posé par la garde de route `RequireAuth`.
 */
export default function LoginPage() {
    const { login, isAuthenticated } = useSession();
    const navigate = useNavigate();
    const location = useLocation();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<unknown>(null);

    const redirectTo = (location.state as { from?: string } | null)?.from ?? paths.account;
    const fields = fieldErrors(error);

    if (isAuthenticated) {
        return <Navigate to={redirectTo} replace />;
    }

    async function handleSubmit(event: FormEvent): Promise<void> {
        event.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            await login({ email, password });
            navigate(redirectTo, { replace: true });
        } catch (caught) {
            setError(caught);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Page title="Connexion">
            <Section>
                <Container className="max-w-md space-y-6">
                    <div className="text-center">
                        <h1 className="font-display text-3xl tracking-tight">Se connecter</h1>
                        <p className="text-ink-soft mt-2 text-sm">
                            Retrouvez votre accès et vos informations de facturation.
                        </p>
                    </div>

                    <Card>
                        <CardBody>
                            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                                <Input
                                    label="Adresse email"
                                    type="email"
                                    required
                                    autoComplete="email"
                                    autoFocus
                                    value={email}
                                    error={fields.email}
                                    onChange={(event) => setEmail(event.target.value)}
                                />

                                <Input
                                    label="Mot de passe"
                                    type="password"
                                    required
                                    autoComplete="current-password"
                                    value={password}
                                    error={fields.password}
                                    onChange={(event) => setPassword(event.target.value)}
                                />

                                {error !== null && Object.keys(fields).length === 0 && (
                                    <Alert tone="critical" title={presentError(error).message}>
                                        {presentError(error).hint}
                                        {isApiError(error, 'invalid_credentials') && (
                                            <p className="mt-2">
                                                <Link to={paths.forgotPassword} className="underline">
                                                    Mot de passe oublié ?
                                                </Link>
                                            </p>
                                        )}
                                    </Alert>
                                )}

                                <Button type="submit" size="lg" className="w-full" loading={submitting}>
                                    Se connecter
                                </Button>
                            </form>
                        </CardBody>
                    </Card>

                    <div className="text-ink-muted space-y-2 text-center text-sm">
                        <p>
                            <Link to={paths.forgotPassword} className="text-brand-700 hover:underline">
                                Mot de passe oublié ou jamais défini ?
                            </Link>
                        </p>
                        <p>
                            Pas encore de compte ?{' '}
                            <Link to={paths.pricing} className="text-brand-700 hover:underline">
                                Il est créé automatiquement lors de votre premier achat.
                            </Link>
                        </p>
                    </div>

                    <DevNote title="Ce que fait cet écran" endpoints={['POST /auth/login']}>
                        <p>
                            La réponse contient un <code className="font-mono">token</code> Bearer et le profil complet,
                            entitlement compris : un seul appel suffit à ouvrir la session et à savoir si l'accès est
                            actif.
                        </p>
                        <p>
                            <strong>Aucun endpoint d'inscription n'existe.</strong> Un compte est provisionné à la
                            réussite d'un paiement (<code className="font-mono">POST /payment/claim</code> ou webhook
                            Stripe), puis un email invite son titulaire à choisir un mot de passe.
                        </p>
                        <p>
                            Cette route est soumise à une limitation de débit plus stricte que le reste de l'API :
                            traitez le code <code className="font-mono">rate_limited</code>.
                        </p>
                    </DevNote>
                </Container>
            </Section>
        </Page>
    );
}
